/**
 * Your profile, as everybody else gets it.
 *
 * Settings used to be two columns of forms with the answer nowhere on the
 * page: you set a banner, a colour, a font and a frame, and then went to
 * your own profile to find out what you had done. Every one of those is a
 * look, and a look has to be looked at.
 *
 * So the forms are one column and this stands beside them, pinned, drawing
 * the same header the profile page draws - the same picture, the same
 * banner, the same name in the same colour and the same font. Whatever a
 * control does happens here at the same moment.
 *
 * Deliberately the header and nothing else. The rest of a profile is bio,
 * games and badges, which are plain text and answer for themselves; the
 * header is the part where three settings overlap and the result is hard to
 * predict.
 */

import { useSession } from '@/stores/session';
import { cn } from '@/lib/cn';
import { absolute } from './user';
import { Avatar } from './Avatar';
import { EmojiText } from './EmojiText';
import { UserName } from './user';
import { Icon } from '@/icons/Icon';

export function ProfilePreview({ className, nameOverride }: {
  className?: string;
  /**
   * What the name controls currently say, before they are saved.
   *
   * Without it this draws what the server has, which is right everywhere
   * except beside the name editor - where the whole point is to see the
   * change you have not committed yet.
   */
  nameOverride?: { nameColor?: string | null; nameColor2?: string | null; nameAnimated?: boolean; nameFont?: string | null };
}) {
  const me = useSession((s) => s.me);
  if (!me) return null;

  const banner = me.banner ? absolute(me.banner) : null;
  const who = { ...me, ...(nameOverride ?? {}) };

  return (
    <div className={cn('overflow-hidden rounded-[var(--radius-l)] border border-line-soft bg-surface-1', className)}>
      {/* The banner, at the shape the profile page uses. */}
      <div
        className="relative h-[92px] overflow-hidden"
        style={{
          background: banner
            ? 'var(--bg-3)'
            : me.bannerColor
              ? `linear-gradient(115deg, ${me.bannerColor}, color-mix(in srgb, ${me.bannerColor} 45%, var(--bg-3)))`
              : 'linear-gradient(115deg, color-mix(in srgb, var(--accent) 42%, var(--bg-3)), color-mix(in srgb, var(--accent) 12%, var(--bg-3)) 70%)'
        }}
      >
        {banner ? <img src={banner} alt="" className="h-full w-full object-cover" /> : null}
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[62%]"
          style={{ background: 'linear-gradient(180deg, transparent, color-mix(in srgb, var(--bg-1) 88%, transparent))' }}
        />
      </div>

      <div className="px-3 pb-3">
        <div className="-mt-[30px] flex items-end gap-3">
          <span className="flex-none leading-[0]">
            <Avatar user={me} size={64} presence />
          </span>
          <div className="min-w-0 flex-1 pb-1">
            <UserName user={who} className="block truncate text-[18px] font-extrabold" profile={false} />
            <p className="m-0 truncate text-[12px] text-mute">@{me.username}</p>
          </div>
        </div>

        {me.customStatus ? (
          <p className="mt-2 mb-0 truncate text-[13px] text-dim">
            <EmojiText text={me.customStatus} size={15} />
          </p>
        ) : null}

        {me.bio ? (
          <p className="mt-2 mb-0 line-clamp-2 text-[12.5px] text-mute">{me.bio}</p>
        ) : null}

        <p className="mt-2.5 mb-0 flex items-center gap-1.5 text-[11px] text-ghost">
          <Icon name="eye" size={12} />
          This is what people see
        </p>
      </div>
    </div>
  );
}
